import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import './index.css';

// רשימת המחלקות בסופר
const DEFAULT_CATEGORIES = [
  'פירות וירקות',
  'מוצרי חלב',
  'בשר ודגים',
  'מאפים ולחמים',
  'קפואים',
  'שימורים ויבשים',
  'חטיפים ומשקאות',
  'ניקיון',
  'טואלטיקה',
  'תינוקות',
  'אחר'
];

// צבע לכל מחלקה
const CATEGORY_STYLES = {
  'פירות וירקות': 'cat-green',
  'מוצרי חלב': 'cat-blue',
  'בשר ודגים': 'cat-red',
  'מאפים ולחמים': 'cat-orange',
  'קפואים': 'cat-cyan',
  'שימורים ויבשים': 'cat-purple',
  'חטיפים ומשקאות': 'cat-pink',
  'ניקיון': 'cat-teal',
  'טואלטיקה': 'cat-violet',
  'תינוקות': 'cat-rose',
  'אחר': 'cat-gray'
};

// localStorage
const LOCAL_ITEMS_KEY = 'shopping_items_cache';
const LOCAL_PENDING_KEY = 'shopping_pending_changes';
const LOCAL_CATEGORIES_KEY = 'shopping_categories_cache';
const LOCAL_STORES_KEY = 'shopping_stores_cache';

const DEFAULT_STORES = ['כללי', 'רמי לוי', 'שופרסל', 'ויקטורי', 'אושר עד'];

function saveLocalItems(items) {
  try {
    localStorage.setItem(LOCAL_ITEMS_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('Cannot save local items:', e);
  }
}

function loadLocalItems() {
  try {
    const raw = localStorage.getItem(LOCAL_ITEMS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('Cannot load local items:', e);
    return null;
  }
}

function savePendingChanges(changes) {
  try {
    localStorage.setItem(LOCAL_PENDING_KEY, JSON.stringify(changes));
  } catch (e) {
    console.warn('Cannot save pending changes:', e);
  }
}

function loadPendingChanges() {
  try {
    const raw = localStorage.getItem(LOCAL_PENDING_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.warn('Cannot load pending changes:', e);
    return [];
  }
}

function saveLocalList(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {
    console.warn('Cannot save local list:', e);
  }
}

function loadLocalList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch (e) {
    console.warn('Cannot load local list:', e);
  }
  return fallback;
}

function normalizeListValue(value) {
  return String(value || '').trim();
}

function uniqueList(list) {
  const seen = new Set();
  return list
    .map(normalizeListValue)
    .filter(Boolean)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

function mergeRemoteWithLocal(remoteItems, localItems) {
  const localById = new Map((localItems || []).map((item) => [String(item.id), item]));

  return (remoteItems || []).map((remote, index) => {
    const local = localById.get(String(remote.id));
    return {
      ...remote,
      store: remote.store ?? local?.store ?? 'כללי',
      position: remote.position ?? local?.position ?? (index + 1) * 1000
    };
  });
}


function getItemPosition(item, fallbackIndex = 0) {
  if (typeof item.position === 'number') return item.position;
  if (item.position !== null && item.position !== undefined && !Number.isNaN(Number(item.position))) {
    return Number(item.position);
  }
  return fallbackIndex * 1000;
}

function sortItemsInsideCategory(list) {
  return [...list].sort((a, b) => {
    const posDiff = getItemPosition(a) - getItemPosition(b);
    if (posDiff !== 0) return posDiff;
    const t1 = new Date(a.created_at || 0).getTime();
    const t2 = new Date(b.created_at || 0).getTime();
    return t2 - t1;
  });
}

function compactPositions(itemsInCategory) {
  return itemsInCategory.map((item, index) => ({
    ...item,
    position: (index + 1) * 1000
  }));
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState('signin');

  const [items, setItems] = useState(() => loadLocalItems() || []);
  const [categories, setCategories] = useState(() => loadLocalList(LOCAL_CATEGORIES_KEY, DEFAULT_CATEGORIES));
  const [stores, setStores] = useState(() => loadLocalList(LOCAL_STORES_KEY, DEFAULT_STORES));
  const [newItem, setNewItem] = useState('');
  const [newCategory, setNewCategory] = useState('אחר');
  const [newStore, setNewStore] = useState('כללי');
  const [showOnlyTodo, setShowOnlyTodo] = useState(true);
  const [sortMode, setSortMode] = useState('time');
  const [loading, setLoading] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [syncMessage, setSyncMessage] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [quickBuyMode, setQuickBuyMode] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('אחר');
  const [editStore, setEditStore] = useState('כללי');
  const [showManagePanel, setShowManagePanel] = useState(false);
  const [newManagedStore, setNewManagedStore] = useState('');
  const [newManagedCategory, setNewManagedCategory] = useState('');
  const swipeRef = useRef(null);

  const getCategoryIndex = (category) => {
    const index = categories.indexOf(category || 'אחר');
    return index === -1 ? categories.length : index;
  };

  const rememberSyncIssue = (message, error) => {
    if (error) console.error(message, error);
    setSyncMessage(message);
  };

  const clearSyncIssue = () => {
    setSyncMessage('');
  };

  const isMissingColumn = (error, columnName) => {
    const text = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`.toLowerCase();
    return text.includes(columnName.toLowerCase()) && (
      text.includes('column') ||
      text.includes('schema cache') ||
      text.includes('could not find') ||
      text.includes('does not exist')
    );
  };

  const removeUnsupportedColumns = (payload, error) => {
    const nextPayload = { ...payload };
    if (isMissingColumn(error, 'position')) delete nextPayload.position;
    if (isMissingColumn(error, 'store')) delete nextPayload.store;
    return nextPayload;
  };

  const insertShoppingItem = async (payload) => {
    let currentPayload = { ...payload };

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const { data, error } = await supabase
        .from('shopping_items')
        .insert([currentPayload])
        .select('*')
        .single();

      if (!error) return { data: { ...data, store: payload.store ?? data?.store ?? 'כללי', position: payload.position ?? data?.position }, error: null };

      const nextPayload = removeUnsupportedColumns(currentPayload, error);
      if (JSON.stringify(nextPayload) === JSON.stringify(currentPayload)) return { data: null, error };
      currentPayload = nextPayload;
    }

    return { data: null, error: new Error('insert failed after retry') };
  };

  const updateShoppingItem = async (id, payload) => {
    let currentPayload = { ...payload };

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const { error } = await supabase
        .from('shopping_items')
        .update(currentPayload)
        .eq('id', id);

      if (!error) return { error: null };

      const nextPayload = removeUnsupportedColumns(currentPayload, error);
      if (JSON.stringify(nextPayload) === JSON.stringify(currentPayload)) return { error };
      currentPayload = nextPayload;
    }

    return { error: new Error('update failed after retry') };
  };

  const selectShoppingItems = async () => {
    let result = await supabase
      .from('shopping_items')
      .select('*')
      .order('category', { ascending: true })
      .order('position', { ascending: true })
      .order('created_at', { ascending: false });

    if (result.error && isMissingColumn(result.error, 'position')) {
      result = await supabase
        .from('shopping_items')
        .select('*')
        .order('category', { ascending: true })
        .order('created_at', { ascending: false });
    }

    return result;
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          console.error('auth getUser error:', error);
        }
        setUser(data?.user ?? null);
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);


  useEffect(() => {
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsInstalled(Boolean(isStandalone));

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstalled(true);
    }
  };


  useEffect(() => {
    saveLocalList(LOCAL_CATEGORIES_KEY, categories);
  }, [categories]);

  useEffect(() => {
    saveLocalList(LOCAL_STORES_KEY, stores);
  }, [stores]);

  const handleSignUp = async () => {
    if (!email || !password) return;
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) {
      console.error('Sign up error:', error);
      alert('שגיאה בהרשמה: ' + error.message);
    } else {
      alert('נרשמת בהצלחה, עכשיו אפשר להתחבר');
      setAuthMode('signin');
    }
  };

  const handleSignIn = async () => {
    if (!email || !password) return;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Sign in error:', error);
      alert('שגיאה בהתחברות: ' + error.message);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setItems([]);
    saveLocalItems([]);
    savePendingChanges([]);
  };

  const syncPendingChanges = async (currentUser) => {
    if (!currentUser || !navigator.onLine) return;

    const pending = loadPendingChanges();
    if (!pending.length) return;

    const stillPending = [];

    for (const change of pending) {
      try {
        if (change.type === 'toggle') {
          const { error } = await updateShoppingItem(change.id, { is_done: change.is_done });
          if (error) stillPending.push(change);
        } else if (change.type === 'delete') {
          const { error } = await supabase
            .from('shopping_items')
            .delete()
            .eq('id', change.id);

          if (error) stillPending.push(change);
        } else if (change.type === 'category') {
          const { error } = await updateShoppingItem(change.id, {
            category: change.category,
            position: change.position
          });

          if (error) stillPending.push(change);
        } else if (change.type === 'edit') {
          const { error } = await updateShoppingItem(change.id, {
            name: change.name,
            category: change.category,
            store: change.store,
            position: change.position
          });

          if (error) stillPending.push(change);
        } else if (change.type === 'position') {
          let failed = false;
          for (const row of change.items) {
            const { error } = await updateShoppingItem(row.id, { position: row.position });
            if (error) failed = true;
          }
          if (failed) stillPending.push(change);
        } else if (change.type === 'add') {
          const { data, error } = await insertShoppingItem({
            name: change.name,
            category: change.category,
            store: change.store || 'כללי',
            is_done: change.is_done,
            position: change.position,
            user_id: currentUser.id
          });

          if (error) {
            stillPending.push(change);
          } else {
            const cached = loadLocalItems() || [];
            const updatedCached = cached.map((it) =>
              it.id === change.tempId ? data : it
            );
            saveLocalItems(updatedCached);
          }
        }
      } catch (e) {
        stillPending.push(change);
      }
    }

    savePendingChanges(stillPending);
  };

  const fetchItems = async (currentUser) => {
    if (!currentUser) return;

    const cached = loadLocalItems();
    if (cached && cached.length && items.length === 0) {
      setItems(cached);
    }

    if (!navigator.onLine) {
      setOffline(true);
      if (cached) setItems(cached);
      return;
    }

    try {
      await syncPendingChanges(currentUser);

      const { data, error } = await selectShoppingItems();

      if (error) throw error;

      clearSyncIssue();
      setOffline(false);
      const mergedItems = mergeRemoteWithLocal(data || [], loadLocalItems() || []);
      setItems(mergedItems);
      saveLocalItems(mergedItems);
    } catch (error) {
      console.error('❌ fetchItems error:', error);
      setOffline(true);
      rememberSyncIssue('יש בעיית סנכרון מול Supabase. מוצגת גרסה מקומית מהזיכרון.', error);

      const latestCached = loadLocalItems();
      if (latestCached) {
        setItems(latestCached);
      }
    }
  };

  useEffect(() => {
    if (!user) return;

    setOffline(!navigator.onLine);
    fetchItems(user);

    const channel = supabase
      .channel('shopping_items_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shopping_items' },
        () => {
          if (navigator.onLine) {
            fetchItems(user);
          }
        }
      )
      .subscribe();

    const handleOnline = async () => {
      setOffline(false);
      clearSyncIssue();
      await syncPendingChanges(user);
      await fetchItems(user);
    };

    const handleOffline = () => {
      setOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const addItem = async () => {
    if (!newItem.trim() || !user) return;

    const itemName = newItem.trim();
    const category = newCategory || 'אחר';
    const store = newStore || 'כללי';
    const sameCategoryItems = items.filter((it) => (it.category || 'אחר') === category);
    const nextPosition = sameCategoryItems.length
      ? Math.max(...sameCategoryItems.map((it, index) => getItemPosition(it, index))) + 1000
      : 1000;

    setLoading(true);

    try {
      const { data, error } = await insertShoppingItem({
        name: itemName,
        is_done: false,
        category,
        store,
        position: nextPosition,
        user_id: user.id
      });

      if (error) throw error;

      setNewItem('');
      setNewCategory('אחר');
      setNewStore('כללי');

      const newItems = [data, ...items];
      setItems(newItems);
      saveLocalItems(newItems);
      clearSyncIssue();
    } catch (error) {
      const tempId = `temp-${Date.now()}`;
      const tempItem = {
        id: tempId,
        name: itemName,
        is_done: false,
        category,
        store,
        position: nextPosition,
        user_id: user.id,
        created_at: new Date().toISOString()
      };

      const newItems = [tempItem, ...items];
      setItems(newItems);
      saveLocalItems(newItems);

      const pending = loadPendingChanges();
      pending.push({
        type: 'add',
        tempId,
        name: itemName,
        category,
        store,
        position: nextPosition,
        is_done: false
      });
      savePendingChanges(pending);
      if (!navigator.onLine) {
        setOffline(true);
        rememberSyncIssue('המוצר נשמר מקומית, ויסונכרן כשיהיה חיבור.', error);
      } else {
        rememberSyncIssue('המוצר נשמר מקומית, אבל עדיין לא סונכרן לענן.', error);
      }

      setNewItem('');
      setNewCategory('אחר');
      setNewStore('כללי');
    } finally {
      setLoading(false);
    }
  };

  const toggleDone = async (item) => {
    if (!user) return;

    const updated = !item.is_done;

    const newItems = items.map((it) =>
      it.id === item.id ? { ...it, is_done: updated } : it
    );
    setItems(newItems);
    saveLocalItems(newItems);

    const change = { type: 'toggle', id: item.id, is_done: updated };

    try {
      const { error } = await supabase
        .from('shopping_items')
        .update({ is_done: updated })
        .eq('id', item.id);

      if (error) throw error;
      clearSyncIssue();
    } catch (error) {
      const pending = loadPendingChanges();
      pending.push(change);
      savePendingChanges(pending);
      rememberSyncIssue('הסימון נשמר מקומית, אבל עדיין לא סונכרן לענן.', error);
    }
  };

  const deleteItem = async (id) => {
    if (!user) return;

    const newItems = items.filter((it) => it.id !== id);
    setItems(newItems);
    saveLocalItems(newItems);

    const change = { type: 'delete', id };

    try {
      const { error } = await supabase
        .from('shopping_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      clearSyncIssue();
    } catch (error) {
      const pending = loadPendingChanges();
      pending.push(change);
      savePendingChanges(pending);
      rememberSyncIssue('המחיקה נשמרה מקומית, אבל עדיין לא סונכרנה לענן.', error);
    }
  };

  const savePositionsInBackground = async (orderedItems, optimisticItems) => {
    const cleanRows = orderedItems
      .filter((item) => !String(item.id).startsWith('temp-'))
      .map((item) => ({ id: item.id, position: item.position }));

    if (!cleanRows.length) return;

    const change = { type: 'position', items: cleanRows };

    try {
      let failed = false;
      for (const row of cleanRows) {
        const { error } = await updateShoppingItem(row.id, { position: row.position });
        if (error) failed = true;
      }
      if (failed) throw new Error('position update failed');
      clearSyncIssue();
    } catch (error) {
      const pending = loadPendingChanges();
      pending.push(change);
      savePendingChanges(pending);
      saveLocalItems(optimisticItems);
      rememberSyncIssue('הסדר החדש נשמר במסך, אבל עדיין לא סונכרן לענן.', error);
    }
  };

  const reorderCategory = async (category, orderedIds) => {
    const categoryItems = items.filter((it) => (it.category || 'אחר') === category);
    const byId = new Map(categoryItems.map((it) => [String(it.id), it]));
    const orderedVisible = orderedIds.map((id) => byId.get(String(id))).filter(Boolean);
    const hiddenItems = categoryItems.filter((it) => !orderedIds.includes(String(it.id)));
    const newCategoryOrder = compactPositions([...orderedVisible, ...hiddenItems]);
    const updatedMap = new Map(newCategoryOrder.map((it) => [String(it.id), it]));
    const newItems = items.map((it) => updatedMap.get(String(it.id)) || it);

    setItems(newItems);
    saveLocalItems(newItems);
    await savePositionsInBackground(newCategoryOrder, newItems);
  };

  const moveItemInCategory = async (category, itemId, direction) => {
    const visibleInCategory = sortItemsInsideCategory(
      visibleItems.filter((it) => (it.category || 'אחר') === category)
    );
    const index = visibleInCategory.findIndex((it) => String(it.id) === String(itemId));
    const newIndex = index + direction;
    if (index < 0 || newIndex < 0 || newIndex >= visibleInCategory.length) return;

    const reordered = [...visibleInCategory];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);
    await reorderCategory(category, reordered.map((it) => String(it.id)));
  };

  const handleDragStart = (item, category) => {
    if (navigator.vibrate) navigator.vibrate(12);
    setDraggedItem({ id: String(item.id), category });
  };

  const handleDropOnItem = async (targetItem, category) => {
    if (!draggedItem || draggedItem.category !== category) {
      setDraggedItem(null);
      setDragOverItemId(null);
      return;
    }

    const sourceId = draggedItem.id;
    const targetId = String(targetItem.id);
    if (sourceId === targetId) {
      setDraggedItem(null);
      setDragOverItemId(null);
      return;
    }

    const visibleInCategory = sortItemsInsideCategory(
      visibleItems.filter((it) => (it.category || 'אחר') === category)
    );
    const reordered = [...visibleInCategory];
    const fromIndex = reordered.findIndex((it) => String(it.id) === sourceId);
    const toIndex = reordered.findIndex((it) => String(it.id) === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggedItem(null);
      setDragOverItemId(null);
      return;
    }

    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setDraggedItem(null);
    setDragOverItemId(null);
    await reorderCategory(category, reordered.map((it) => String(it.id)));
  };


  const handleDragEnterItem = (item, category) => {
    if (!draggedItem || draggedItem.category !== category) return;
    if (draggedItem.id !== String(item.id)) {
      setDragOverItemId(String(item.id));
    }
  };

  const updateCategory = async (item, category) => {
    if (!user) return;

    setEditingCategoryId(null);

    const sameCategoryItems = items.filter((it) => (it.category || 'אחר') === category && it.id !== item.id);
    const nextPosition = sameCategoryItems.length
      ? Math.max(...sameCategoryItems.map((it, index) => getItemPosition(it, index))) + 1000
      : 1000;

    const newItems = items.map((it) =>
      it.id === item.id ? { ...it, category, position: nextPosition } : it
    );
    setItems(newItems);
    saveLocalItems(newItems);

    const change = { type: 'category', id: item.id, category, position: nextPosition };

    try {
      const { error } = await updateShoppingItem(item.id, { category, position: nextPosition });

      if (error) throw error;
      clearSyncIssue();
    } catch (error) {
      const pending = loadPendingChanges();
      pending.push(change);
      savePendingChanges(pending);
      rememberSyncIssue('שינוי הקטגוריה נשמר מקומית, אבל עדיין לא סונכרן לענן.', error);
    }
  };

  const startEditItem = (item) => {
    setEditingItemId(item.id);
    setEditName(item.name || '');
    setEditCategory(item.category || 'אחר');
    setEditStore(item.store || 'כללי');
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
    setEditName('');
    setEditCategory('אחר');
    setEditStore('כללי');
  };

  const saveEditItem = async (item) => {
    const name = editName.trim();
    if (!name || !user) return;

    const categoryChanged = (item.category || 'אחר') !== editCategory;
    const sameCategoryItems = items.filter((it) => (it.category || 'אחר') === editCategory && it.id !== item.id);
    const nextPosition = categoryChanged
      ? (sameCategoryItems.length ? Math.max(...sameCategoryItems.map((it, index) => getItemPosition(it, index))) + 1000 : 1000)
      : getItemPosition(item);

    const updatedItem = {
      ...item,
      name,
      category: editCategory,
      store: editStore || 'כללי',
      position: nextPosition
    };

    const newItems = items.map((it) => it.id === item.id ? updatedItem : it);
    setItems(newItems);
    saveLocalItems(newItems);
    cancelEditItem();

    const change = {
      type: 'edit',
      id: item.id,
      name,
      category: editCategory,
      store: editStore || 'כללי',
      position: nextPosition
    };

    try {
      const { error } = await updateShoppingItem(item.id, {
        name,
        category: editCategory,
        store: editStore || 'כללי',
        position: nextPosition
      });

      if (error) throw error;
      clearSyncIssue();
    } catch (error) {
      const pending = loadPendingChanges();
      pending.push(change);
      savePendingChanges(pending);
      rememberSyncIssue('עריכת המוצר נשמרה מקומית, אבל עדיין לא סונכרנה לענן.', error);
    }
  };

  const addManagedStore = () => {
    const value = normalizeListValue(newManagedStore);
    if (!value) return;
    setStores((current) => uniqueList([...current, value]));
    setNewManagedStore('');
  };

  const removeManagedStore = (storeName) => {
    if (storeName === 'כללי') return;
    if (items.some((item) => (item.store || 'כללי') === storeName)) {
      alert('אי אפשר למחוק חנות שיש אליה מוצרים. קודם שנה את החנות במוצרים האלה.');
      return;
    }
    setStores((current) => current.filter((store) => store !== storeName));
  };

  const addManagedCategory = () => {
    const value = normalizeListValue(newManagedCategory);
    if (!value) return;
    setCategories((current) => uniqueList([...current, value]));
    setNewManagedCategory('');
  };

  const removeManagedCategory = (categoryName) => {
    if (categoryName === 'אחר') return;
    if (items.some((item) => (item.category || 'אחר') === categoryName)) {
      alert('אי אפשר למחוק מחלקה שיש אליה מוצרים. קודם שנה את המחלקה במוצרים האלה.');
      return;
    }
    setCategories((current) => current.filter((category) => category !== categoryName));
  };

  const handleSwipeStart = (event, item) => {
    const target = event.target;
    if (target.closest('button') || target.closest('select') || target.closest('input')) return;

    swipeRef.current = {
      item,
      x: event.clientX,
      y: event.clientY,
      time: Date.now()
    };
  };

  const handleSwipeEnd = (event, item) => {
    if (!swipeRef.current || String(swipeRef.current.item.id) !== String(item.id)) return;

    const dx = event.clientX - swipeRef.current.x;
    const dy = event.clientY - swipeRef.current.y;
    const dt = Date.now() - swipeRef.current.time;
    swipeRef.current = null;

    if (dx > 80 && Math.abs(dy) < 60 && dt < 900 && !item.is_done) {
      if (navigator.vibrate) navigator.vibrate(25);
      toggleDone(item);
    }
  };

  const getVisibleItems = () => {
    let filtered = [...items];

    if (showOnlyTodo) {
      filtered = filtered.filter((item) => !item.is_done);
    }

    if (sortMode === 'status') {
      filtered.sort((a, b) => {
        if (a.is_done !== b.is_done) return a.is_done ? 1 : -1;
        const catDiff = getCategoryIndex(a.category) - getCategoryIndex(b.category);
        if (catDiff !== 0) return catDiff;
        return getItemPosition(a) - getItemPosition(b);
      });
    } else {
      filtered.sort((a, b) => {
        const catDiff = getCategoryIndex(a.category) - getCategoryIndex(b.category);
        if (catDiff !== 0) return catDiff;
        return getItemPosition(a) - getItemPosition(b);
      });
    }

    return filtered;
  };

  const visibleItems = getVisibleItems();

  const groupedByCategory = visibleItems.reduce((acc, item) => {
    const cat = item.category || 'אחר';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  Object.keys(groupedByCategory).forEach((cat) => {
    groupedByCategory[cat] = sortItemsInsideCategory(groupedByCategory[cat]);
  });

  const displayCategories = uniqueList([...categories, ...Object.keys(groupedByCategory)]);

  const itemSuggestions = useMemo(() => {
    const uniqueNames = new Set();

    items.forEach((item) => {
      const name = item?.name?.trim();
      if (name) uniqueNames.add(name);
    });

    return [...uniqueNames].sort((a, b) => a.localeCompare(b, 'he'));
  }, [items]);

  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => i.is_done).length;
    const todo = total - done;
    const progress = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, done, todo, progress };
  }, [items]);


  const pendingCount = loadPendingChanges().length;
  const syncStatus = offline
    ? { label: 'ממתין לחיבור', icon: '📡', tone: 'offline' }
    : syncMessage
      ? { label: 'בעיית סנכרון', icon: '⚠️', tone: 'warning' }
      : pendingCount > 0
        ? { label: 'מסנכרן', icon: '🔄', tone: 'syncing' }
        : { label: 'מסונכרן', icon: '✅', tone: 'ok' };

  if (authLoading) {
    return (
      <div className="app-shell">
        <div className="auth-wrap">
          <div className="auth-card">
            <h2>טוען...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-shell">
        <div className="auth-wrap">
          <div className="auth-card">
            <div className="hero-badge">Family Shopping</div>
            <h1 className="auth-title">רשימת קניות למשפחה</h1>
            <p className="auth-subtitle">
              התחברו כדי לנהל רשימה משותפת, מסודרת לפי מחלקות, עם עבודה גם באופליין.
            </p>

            <div className="auth-form">
              <input
                className="auth-input"
                type="email"
                placeholder="אימייל"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                className="auth-input"
                type="password"
                placeholder="סיסמה"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {authMode === 'signin' ? (
                <>
                  <button className="primary-btn large-btn" onClick={handleSignIn}>
                    התחברות
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => setAuthMode('signup')}
                  >
                    אין לכם משתמש? הרשמה
                  </button>
                </>
              ) : (
                <>
                  <button className="primary-btn large-btn" onClick={handleSignUp}>
                    הרשמה
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => setAuthMode('signin')}
                  >
                    כבר יש לכם משתמש? התחברות
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell ${quickBuyMode ? 'quick-buy-shell' : ''}`}>
      <div className="app">
        <header className="topbar">
          <div>
            <div className="topbar-kicker">Family Space</div>
            <h1 className="page-title">🛒 רשימת קניות חכמה</h1>
            <p className="topbar-subtitle">{user.email}</p>
          </div>

          <div className="topbar-actions">
            <div className={`sync-indicator ${syncStatus.tone}`} title={syncMessage || syncStatus.label}>
              <span className="sync-icon">{syncStatus.icon}</span>
              <span>{syncStatus.label}</span>
            </div>

            {installPrompt && !isInstalled && (
              <button className="install-btn" onClick={handleInstallApp}>
                📲 התקן אפליקציה
              </button>
            )}

            <button className="ghost-btn small-btn" onClick={handleSignOut}>
              התנתק
            </button>
          </div>
        </header>

        <section className="summary-card">
          <div className="summary-row">
            <div className="summary-box">
              <span className="summary-label">סה״כ</span>
              <strong>{stats.total}</strong>
            </div>
            <div className="summary-box">
              <span className="summary-label">נשאר</span>
              <strong>{stats.todo}</strong>
            </div>
            <div className="summary-box">
              <span className="summary-label">נקנה</span>
              <strong>{stats.done}</strong>
            </div>
          </div>

          <div className="progress-wrap">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${stats.progress}%` }}
              ></div>
            </div>
            <span className="progress-text">{stats.progress}% הושלמו</span>
          </div>
        </section>

        <section className="main-card">
          <div className="add-panel">
            <input
              className="item-input"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="מה צריך לקנות?"
              list="item-suggestions"
              autoComplete="off"
              onKeyDown={(e) => e.key === 'Enter' && addItem()}
            />

            <datalist id="item-suggestions">
              {itemSuggestions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>

            <select
              className="category-select"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              className="category-select"
              value={newStore}
              onChange={(e) => setNewStore(e.target.value)}
            >
              {stores.map((store) => (
                <option key={store} value={store}>
                  {store}
                </option>
              ))}
            </select>

            <button className="primary-btn add-btn" onClick={addItem} disabled={loading}>
              {loading ? '...' : 'הוסף'}
            </button>
          </div>

          <div className="toolbar">
            <button
              className={`mode-chip ${showOnlyTodo ? 'active' : ''}`}
              onClick={() => setShowOnlyTodo(!showOnlyTodo)}
            >
              {showOnlyTodo ? 'הצג הכל' : 'מצב סופר 🛒'}
            </button>

            <button
              className={`mode-chip ${quickBuyMode ? 'active quick-active' : ''}`}
              onClick={() => setQuickBuyMode(!quickBuyMode)}
            >
              {quickBuyMode ? 'צא מקנייה מהירה' : 'קנייה מהירה ⚡'}
            </button>

            <select
              className="sort-select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
            >
              <option value="time">מיון: סדר ידני</option>
              <option value="status">מיון: לפי סטטוס</option>
            </select>

            <button
              className={`mode-chip ${showManagePanel ? 'active' : ''}`}
              onClick={() => setShowManagePanel(!showManagePanel)}
            >
              ניהול חנויות ומחלקות ⚙️
            </button>
          </div>

          {showManagePanel && (
            <div className="manage-panel">
              <div className="manage-column">
                <h3>חנויות</h3>
                <div className="manage-add-row">
                  <input
                    className="item-input compact"
                    value={newManagedStore}
                    onChange={(e) => setNewManagedStore(e.target.value)}
                    placeholder="שם חנות חדשה"
                    onKeyDown={(e) => e.key === 'Enter' && addManagedStore()}
                  />
                  <button className="primary-btn small-btn" onClick={addManagedStore}>הוסף</button>
                </div>
                <div className="manage-chips">
                  {stores.map((store) => (
                    <span key={store} className="manage-chip">
                      {store}
                      <button onClick={() => removeManagedStore(store)} disabled={store === 'כללי'} title="מחק חנות">×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="manage-column">
                <h3>מחלקות</h3>
                <div className="manage-add-row">
                  <input
                    className="item-input compact"
                    value={newManagedCategory}
                    onChange={(e) => setNewManagedCategory(e.target.value)}
                    placeholder="שם מחלקה חדשה"
                    onKeyDown={(e) => e.key === 'Enter' && addManagedCategory()}
                  />
                  <button className="primary-btn small-btn" onClick={addManagedCategory}>הוסף</button>
                </div>
                <div className="manage-chips">
                  {categories.map((category) => (
                    <span key={category} className="manage-chip">
                      {category}
                      <button onClick={() => removeManagedCategory(category)} disabled={category === 'אחר'} title="מחק מחלקה">×</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {visibleItems.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🧺</div>
              <div className="empty-title">אין פריטים להצגה</div>
              <div className="empty-subtitle">
                הוסיפו מוצר חדש או עברו ל״הצג הכל״ כדי לראות גם מה שכבר נקנה.
              </div>
            </div>
          )}

          <div className="categories-grid">
            {displayCategories.map((cat) => {
              const itemsInCat = groupedByCategory[cat];
              if (!itemsInCat || itemsInCat.length === 0) return null;

              return (
                <section
                  key={cat}
                  className={`category-card ${CATEGORY_STYLES[cat] || 'cat-gray'}`}
                >
                  <div className="category-card-header">
                    <div>
                      <div className="category-card-title">{cat}</div>
                      <div className="category-card-count">
                        {itemsInCat.length} פריטים
                      </div>
                    </div>
                  </div>

                  <ul className="items-list">
                    {itemsInCat.map((item, index) => (
                      <li
                        key={item.id}
                        className={`item-card ${item.is_done ? 'done' : ''} ${draggedItem?.id === String(item.id) ? 'dragging' : ''} ${dragOverItemId === String(item.id) ? 'drag-over' : ''}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={() => handleDragEnterItem(item, cat)}
                        onDrop={() => handleDropOnItem(item, cat)}
                        onPointerDown={(e) => handleSwipeStart(e, item)}
                        onPointerUp={(e) => handleSwipeEnd(e, item)}
                      >
                        <div className="swipe-hint">החלק ימינה לסימון כרכוש</div>

                        {editingItemId === item.id ? (
                          <div className="item-edit-form">
                            <input
                              className="item-input edit-name-input"
                              value={editName}
                              autoFocus
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && saveEditItem(item)}
                            />

                            <select
                              className="category-select small"
                              value={editCategory}
                              onChange={(e) => setEditCategory(e.target.value)}
                            >
                              {categories.map((catOption) => (
                                <option key={catOption} value={catOption}>
                                  {catOption}
                                </option>
                              ))}
                            </select>

                            <select
                              className="category-select small"
                              value={editStore}
                              onChange={(e) => setEditStore(e.target.value)}
                            >
                              {stores.map((storeOption) => (
                                <option key={storeOption} value={storeOption}>
                                  {storeOption}
                                </option>
                              ))}
                            </select>

                            <button className="primary-btn small-btn" onClick={() => saveEditItem(item)}>שמור</button>
                            <button className="ghost-btn small-btn" onClick={cancelEditItem}>בטל</button>
                          </div>
                        ) : (
                          <>
                            <div className="item-left">
                              <button
                                type="button"
                                className="drag-handle"
                                title="גרור לשינוי מיקום"
                                draggable
                                onDragStart={() => handleDragStart(item, cat)}
                                onDragEnd={() => setDraggedItem(null)}
                              >
                                ☰
                              </button>

                              <label className="check-label">
                                <input
                                  type="checkbox"
                                  checked={item.is_done}
                                  onChange={() => toggleDone(item)}
                                />
                                <span className="item-text-block">
                                  <span className="item-name">{item.name}</span>
                                  <span className="item-store">{item.store || 'כללי'}</span>
                                </span>
                              </label>
                            </div>

                            <div className="item-right">
                              <div className="order-buttons">
                                <button
                                  type="button"
                                  className="order-btn"
                                  onClick={() => moveItemInCategory(cat, item.id, -1)}
                                  disabled={index === 0}
                                  title="העבר למעלה"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="order-btn"
                                  onClick={() => moveItemInCategory(cat, item.id, 1)}
                                  disabled={index === itemsInCat.length - 1}
                                  title="העבר למטה"
                                >
                                  ↓
                                </button>
                              </div>

                              {editingCategoryId === item.id ? (
                                <select
                                  className="category-select small"
                                  value={item.category || 'אחר'}
                                  autoFocus
                                  onChange={(e) =>
                                    updateCategory(item, e.target.value)
                                  }
                                  onBlur={() => setEditingCategoryId(null)}
                                >
                                  {categories.map((catOption) => (
                                    <option key={catOption} value={catOption}>
                                      {catOption}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <button
                                  type="button"
                                  className="category-pill"
                                  onClick={() => setEditingCategoryId(item.id)}
                                >
                                  {item.category || 'אחר'}
                                </button>
                              )}

                              <button
                                className="ghost-btn small-btn"
                                onClick={() => startEditItem(item)}
                                title="ערוך מוצר"
                              >
                                ערוך
                              </button>

                              <button
                                className="delete-btn"
                                onClick={() => deleteItem(item.id)}
                                title="מחק"
                              >
                                ✕
                              </button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export default App;